import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link } from 'react-router-dom'
import { api } from '../services/api'

interface PersonIdentity {
  id: string
  provider: string
  identifier: string
  is_primary: boolean
}

interface Person {
  id: string
  name: string
  email: string
  title: string | null
  role: string | null
  manager_id: string | null
  user_id: string | null
  is_active: boolean
  created_at: string
  identities?: PersonIdentity[]
  manager?: { id: string; name: string } | null
  has_reports?: boolean
}

interface Goal {
  id: string
  title: string
  description: string | null
  owner_id: string
  assignee_id: string
  project_id: string | null
  status: 'not_started' | 'in_progress' | 'at_risk' | 'complete'
  due_date: string | null
  completed_at: string | null
  owner?: { id: string; name: string }
  project?: { id: string; name: string; project_id: string } | null
  deliverables?: Deliverable[]
}

interface Deliverable {
  id: string
  goal_id: string
  title: string
  assignee_id: string | null
  start_date: string | null
  due_date: string | null
  completed_at: string | null
  location_type: string | null
  location_path: string | null
  verification: string | null
  artifact_url: string | null
  status: 'not_started' | 'in_progress' | 'complete'
  assignee?: { id: string; name: string } | null
}

const statusColors: Record<string, string> = {
  not_started: 'bg-gray-800 text-gray-300',
  in_progress: 'bg-blue-900/50 text-blue-300',
  at_risk: 'bg-red-900/50 text-red-300',
  complete: 'bg-green-900/50 text-green-300',
}

const deliverableStatusColors: Record<string, string> = {
  not_started: 'bg-gray-700 text-gray-400',
  in_progress: 'bg-blue-900/30 text-blue-400',
  complete: 'bg-green-900/30 text-green-400',
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString()
}

interface MyWorkProps {
  isMyWork?: boolean
}

export function People({ isMyWork = false }: MyWorkProps) {
  const { personId } = useParams()

  // For My Work, get the current user's person record
  const { data: myPerson, isLoading: myPersonLoading } = useQuery({
    queryKey: ['people', 'me'],
    queryFn: () => api.get<Person>('/people/me'),
    enabled: isMyWork,
  })

  // Determine which person to show
  const targetPersonId = isMyWork ? myPerson?.id : personId

  // If we have a person ID, show detail view
  if (targetPersonId) {
    return <PersonDetail personId={targetPersonId} isMyWork={isMyWork} />
  }

  // If loading my work
  if (isMyWork && myPersonLoading) {
    return (
      <div className="max-w-4xl">
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </div>
    )
  }

  // If my work but no person linked
  if (isMyWork && !myPerson) {
    return (
      <div className="max-w-4xl">
        <h1 className="text-3xl mb-8">My Work</h1>
        <div className="card">
          <p className="text-[var(--color-text-secondary)]">
            No person record is linked to your account. Contact your administrator to set up your profile.
          </p>
        </div>
      </div>
    )
  }

  // Otherwise show list view
  return <PeopleList />
}

function PeopleList() {
  const { data: people, isLoading } = useQuery({
    queryKey: ['people', 'list'],
    queryFn: () => api.get<Person[]>('/people/admin/people?include_hierarchy=true'),
  })

  if (isLoading) {
    return (
      <div className="max-w-4xl">
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </div>
    )
  }

  // Build tree view - get root people (no manager)
  const rootPeople = people?.filter(p => !p.manager_id) || []

  const renderPersonItem = (person: Person, level: number = 0) => {
    const reports = people?.filter(p => p.manager_id === person.id) || []
    return (
      <div key={person.id}>
        <Link
          to={`/people/${person.id}`}
          className="card-hover block no-underline"
          style={{ marginLeft: `${level * 1.5}rem` }}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">{person.name}</div>
              <div className="text-sm text-[var(--color-text-secondary)]">
                {person.title || person.role || person.email}
              </div>
            </div>
            {!person.is_active && (
              <span className="text-xs px-1.5 py-0.5 bg-yellow-900/50 text-yellow-300 rounded">
                Inactive
              </span>
            )}
          </div>
        </Link>
        {reports.length > 0 && (
          <div className="ml-4 border-l border-[var(--color-border)] pl-2 mt-1 space-y-1">
            {reports.map(report => renderPersonItem(report, level + 1))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="max-w-4xl">
      <h1 className="text-3xl mb-8">People</h1>

      {rootPeople.length > 0 ? (
        <div className="space-y-2">
          {rootPeople.map(person => renderPersonItem(person))}
        </div>
      ) : (
        <div className="card">
          <p className="text-[var(--color-text-secondary)]">
            No people found. Add people in the Admin section.
          </p>
        </div>
      )}
    </div>
  )
}

interface PersonDetailProps {
  personId: string
  isMyWork?: boolean
}

function PersonDetail({ personId, isMyWork }: PersonDetailProps) {
  const [activeTab, setActiveTab] = useState<'goals' | 'activity'>('goals')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const queryClient = useQueryClient()

  const { data: person, isLoading: personLoading } = useQuery({
    queryKey: ['people', personId],
    queryFn: () => api.get<Person>(`/people/${personId}`),
  })

  const { data: goals, isLoading: goalsLoading } = useQuery({
    queryKey: ['people', personId, 'goals', statusFilter],
    queryFn: () => {
      const params = statusFilter ? `?status=${statusFilter}&include_deliverables=true` : '?include_deliverables=true'
      return api.get<Goal[]>(`/people/${personId}/goals${params}`)
    },
    enabled: !!person,
  })

  const updateDeliverableStatus = useMutation({
    mutationFn: ({ deliverableId, status }: { deliverableId: string; status: string }) =>
      api.patch(`/people/admin/deliverables/${deliverableId}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['people', personId, 'goals'] })
    },
  })

  if (personLoading) {
    return (
      <div className="max-w-4xl">
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </div>
    )
  }

  if (!person) {
    return (
      <div className="max-w-4xl">
        <p className="text-red-500">Person not found</p>
      </div>
    )
  }

  // Calculate stats
  const stats = {
    total: goals?.length || 0,
    complete: goals?.filter(g => g.status === 'complete').length || 0,
    inProgress: goals?.filter(g => g.status === 'in_progress').length || 0,
    atRisk: goals?.filter(g => g.status === 'at_risk').length || 0,
    notStarted: goals?.filter(g => g.status === 'not_started').length || 0,
  }

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          {!isMyWork && (
            <Link
              to="/people"
              className="text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)] mb-2 inline-block"
            >
              &larr; All People
            </Link>
          )}
          <h1 className="text-3xl">{isMyWork ? 'My Work' : person.name}</h1>
          {!isMyWork && (
            <div className="text-[var(--color-text-secondary)] mt-1">
              {person.title && <span>{person.title}</span>}
              {person.title && person.role && <span> &middot; </span>}
              {person.role && <span>{person.role}</span>}
            </div>
          )}
          {person.manager && (
            <div className="text-sm text-[var(--color-text-secondary)] mt-1">
              Reports to: <Link to={`/people/${person.manager.id}`} className="hover:underline">{person.manager.name}</Link>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="flex gap-4">
          <div className="text-center">
            <div className="text-2xl font-medium">{stats.total}</div>
            <div className="text-xs text-[var(--color-text-secondary)]">Goals</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-medium text-green-400">{stats.complete}</div>
            <div className="text-xs text-[var(--color-text-secondary)]">Complete</div>
          </div>
          {stats.atRisk > 0 && (
            <div className="text-center">
              <div className="text-2xl font-medium text-red-400">{stats.atRisk}</div>
              <div className="text-xs text-[var(--color-text-secondary)]">At Risk</div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-[var(--color-border)]">
        <button
          onClick={() => setActiveTab('goals')}
          className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'goals'
              ? 'border-[var(--color-brand)] text-[var(--color-text-primary)]'
              : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
          }`}
        >
          Goals
        </button>
        <button
          onClick={() => setActiveTab('activity')}
          className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'activity'
              ? 'border-[var(--color-brand)] text-[var(--color-text-primary)]'
              : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
          }`}
        >
          Activity
        </button>
      </div>

      {activeTab === 'goals' && (
        <div>
          {/* Filter */}
          <div className="flex items-center gap-4 mb-4">
            <label className="text-sm text-[var(--color-text-secondary)]">Filter:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input py-1 text-sm"
            >
              <option value="">All Goals</option>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="at_risk">At Risk</option>
              <option value="complete">Complete</option>
            </select>
          </div>

          {goalsLoading ? (
            <p className="text-[var(--color-text-secondary)]">Loading goals...</p>
          ) : goals && goals.length > 0 ? (
            <div className="space-y-4">
              {goals.map((goal) => (
                <div key={goal.id} className="card">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{goal.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${statusColors[goal.status]}`}>
                          {goal.status.replace('_', ' ')}
                        </span>
                      </div>
                      {goal.description && (
                        <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                          {goal.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-4 text-xs text-[var(--color-text-secondary)] mb-3">
                    {goal.owner && <span>Owner: {goal.owner.name}</span>}
                    {goal.project && (
                      <Link to={`/projects/${goal.project.project_id}`} className="hover:underline">
                        Project: {goal.project.name}
                      </Link>
                    )}
                    {goal.due_date && <span>Due: {formatDate(goal.due_date)}</span>}
                  </div>

                  {/* Deliverables */}
                  {goal.deliverables && goal.deliverables.length > 0 && (
                    <div className="border-t border-[var(--color-border)] pt-3 mt-3">
                      <div className="text-xs text-[var(--color-text-secondary)] uppercase tracking-wide mb-2">
                        Deliverables
                      </div>
                      <div className="space-y-2">
                        {goal.deliverables.map((d) => (
                          <div
                            key={d.id}
                            className="flex items-center justify-between py-2 border-b border-[var(--color-border)] last:border-0"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm">{d.title}</span>
                                {d.location_type && (
                                  <span className="text-xs px-1 py-0.5 bg-[var(--color-surface)] rounded">
                                    {d.location_type}
                                  </span>
                                )}
                              </div>
                              <div className="flex gap-3 text-xs text-[var(--color-text-secondary)] mt-0.5">
                                {d.due_date && <span>Due: {formatDate(d.due_date)}</span>}
                                {d.location_path && (
                                  <code className="truncate max-w-[300px]">{d.location_path}</code>
                                )}
                              </div>
                              {d.verification && (
                                <div className="text-xs text-[var(--color-text-tertiary)] mt-1 italic">
                                  Verify: {d.verification}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {isMyWork ? (
                                <select
                                  value={d.status}
                                  onChange={(e) => updateDeliverableStatus.mutate({
                                    deliverableId: d.id,
                                    status: e.target.value
                                  })}
                                  className={`text-xs px-1.5 py-0.5 rounded border-0 ${deliverableStatusColors[d.status]}`}
                                >
                                  <option value="not_started">Not Started</option>
                                  <option value="in_progress">In Progress</option>
                                  <option value="complete">Complete</option>
                                </select>
                              ) : (
                                <span className={`text-xs px-1.5 py-0.5 rounded ${deliverableStatusColors[d.status]}`}>
                                  {d.status.replace('_', ' ')}
                                </span>
                              )}
                              {d.artifact_url && (
                                <a
                                  href={d.artifact_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-[var(--color-brand)] hover:underline"
                                >
                                  View
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="card">
              <p className="text-[var(--color-text-secondary)]">
                {statusFilter ? 'No goals match the selected filter.' : 'No goals assigned.'}
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'activity' && (
        <PersonActivity identities={person.identities} />
      )}
    </div>
  )
}

interface PersonActivityProps {
  identities?: PersonIdentity[]
}

function PersonActivity({ identities }: PersonActivityProps) {
  // For now, show a placeholder - activity would need to query by the person's identities
  const githubIdentity = identities?.find(i => i.provider === 'github')

  return (
    <div className="card">
      <p className="text-[var(--color-text-secondary)]">
        Activity tracking shows commits, PRs, and file uploads attributed to this person.
      </p>
      {githubIdentity ? (
        <p className="text-sm mt-2">
          GitHub username: <code className="bg-[var(--color-surface)] px-1.5 py-0.5 rounded">{githubIdentity.identifier}</code>
        </p>
      ) : (
        <p className="text-sm text-[var(--color-text-tertiary)] mt-2">
          No GitHub identity linked. Add one in Admin to see GitHub activity.
        </p>
      )}
      <p className="text-xs text-[var(--color-text-tertiary)] mt-4">
        Activity aggregation coming soon. For now, view activity in the Projects page.
      </p>
    </div>
  )
}
